import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./CartModule.scss";
import { CartItem } from "@/shared/components/CartItem";
import { CartSummary } from "@/shared/components/CartSummary";
import { PendingDraftDiscardDialog } from "@/shared/components/PendingDraftDiscardDialog";
import type { CartItem as CartItemType } from "@/shared/types";
import { useCart } from "@/app/providers/CartProvider";

const RESTORE_ERROR_MS = 6000;

interface CartModuleProps {
  onClose: () => void;
  onCheckout: () => void;
}

export const CartModule: React.FC<CartModuleProps> = ({
  onClose,
  onCheckout,
}) => {
  const {
    items,
    updateQuantity,
    removeFromCart,
    totalAmount,
    totalQuantity,
    hasPendingOrderDraft,
    clearPendingOrderDraft,
  } = useCart();

  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [restoreErrorOpen, setRestoreErrorOpen] = useState(false);

  useEffect(() => {
    if (!restoreErrorOpen) return;
    const t = window.setTimeout(() => setRestoreErrorOpen(false), RESTORE_ERROR_MS);
    return () => window.clearTimeout(t);
  }, [restoreErrorOpen]);

  const handleCheckout = () => {
    onCheckout();
  };

  const handleContinueShopping = () => {
    onClose();
  };

  const restoreToast =
    restoreErrorOpen && typeof document !== "undefined"
      ? createPortal(
          <div className="cart-restore-toast" role="alert">
            <span className="cart-restore-toast__icon" aria-hidden>
              !
            </span>
            <span className="cart-restore-toast__message">
              Не удалось восстановить корзину
            </span>
            <button
              type="button"
              className="cart-restore-toast__close"
              aria-label="Закрыть"
              onClick={() => setRestoreErrorOpen(false)}
            >
              ×
            </button>
          </div>,
          document.body
        )
      : null;

  if (!items.length) {
    return (
      <>
        <div className="cart-module cart-module--empty">
          <p>
            {hasPendingOrderDraft
              ? "Похоже, есть незавершенная оплата"
              : "Добавьте товары из каталога"}
          </p>
          <button
            className="cart-module__back-btn"
            onClick={handleContinueShopping}
          >
            Перейти в каталог
          </button>
        </div>
        <PendingDraftDiscardDialog
          open={discardDialogOpen}
          onClose={() => setDiscardDialogOpen(false)}
          onConfirm={clearPendingOrderDraft}
          description="Неоплаченный заказ будет сброшен без возврата товаров в корзину. Продолжить?"
        />
        {restoreToast}
      </>
    );
  }

  return (
    <div className="cart-module">
      <div className="cart-module__items">
        {items.map((item: CartItemType) => (
          <CartItem
            key={item.product.id}
            item={item}
            onUpdateQuantity={updateQuantity}
            onRemove={removeFromCart}
          />
        ))}
      </div>

      <CartSummary
        totalAmount={totalAmount}
        itemsCount={totalQuantity}
        onCheckout={handleCheckout}
        isCheckingOut={false}
      />
    </div>
  );
};
